import { InfoCircle } from '@untitled-ui/icons-react/build/cjs';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ACTIVITY_HINT, ActivityHintType } from 'src/app/MyQuote/AddQuote/multiStop.helpers';
import ServiceLabel from 'src/app/MyQuote/BookingStepper/components/ServiceLabel';
import AdditionalServiceModal from 'src/app/MyQuote/Modals/AdditionalServiceModal';
import ButtonCmp from 'src/components/ButtonCmp';
import CardWithOptions from 'src/components/CardWithOptions';
import CheckBox from 'src/components/CheckBox';
import MapBox from 'src/components/MapBox/MapBox';
import useAddressDetails from 'src/hooks/useAddressDetails';

interface IBookingStopDetailsStop {
    position: number;
    companyName?: string | null;
    fullAddress?: string | null;
    city?: string | null;
    state?: string | null;
    stateCode?: string | null;
    country?: string | null;
    countryCode?: string | null;
    latitude?: string | number | null;
    longitude?: string | number | null;
    activityHint?: ActivityHintType | null;
    serviceIds?: number[];
}

interface IProps {
    stop?: IBookingStopDetailsStop | null;
    stepperFooterRef?: React.MutableRefObject<any>;
}

const ACTIVITY_HINT_OPTIONS: ActivityHintType[] = [ACTIVITY_HINT.PICKUP, ACTIVITY_HINT.DELIVERY, ACTIVITY_HINT.BOTH];
const NON_STOP_SUPPORTED_SLUGS = ['in-bond', 'load_insurance', 'dangerous_goods'];

const getActivityHintLabel = (t: any, value: ActivityHintType): string => {
    if (value === ACTIVITY_HINT.PICKUP) return t('MyQuote.Activity_Pickup');
    if (value === ACTIVITY_HINT.DELIVERY) return t('MyQuote.Activity_Delivery');

    return t('MyQuote.Activity_Both');
};

/**
 * BookingStopDetails — read-only display of one middle stop (position 2..N-1) inside the Book
 * Quote stepper. Client decision — mirrors AddQuote/Steps/StopDetails/index.tsx's actual field set
 * exactly (activity hint + address/map + Pickup/Delivery Additional Services) since that's the
 * established convention for a middle stop everywhere else in the app — NOT the Shipper/Consignee
 * date/window fields a middle stop never has. Every control here is disabled (no pencil-edit icons,
 * no address picker, no service toggling) and none of it is ever added to the booking submission
 * payload (see ReviewFinalize.tsx's payload object — it lists its fields explicitly and never
 * references `stops`, so this step's data structurally can't leak into it).
 */
const BookingStopDetails = ({ stop, stepperFooterRef }: IProps) => {
    const { t } = useTranslation();
    const { serviceTypes } = useAddressDetails();
    const [serviceInfoModal, setServiceInfoModal] = useState<'pickup' | 'delivery' | null>(null);

    // Bug fix (this feature) — stepperFooterRef is a single mutable ref shared across every step
    // component; the previously-active step (e.g. Shipper Details) leaves ITS triggerNext/
    // triggerBack registered there. Without resetting it here, clicking Next/Back while sitting on
    // a stop step would silently re-run that unrelated step's save logic. This step has nothing to
    // validate or save, so both handlers are plain no-ops.
    useEffect(() => {
        if (stepperFooterRef && 'current' in stepperFooterRef) {
            stepperFooterRef.current = {
                triggerNext: async () => {},
                triggerBack: async () => {},
            };
        }
    }, [stepperFooterRef]);

    if (!stop) return null;

    const activityHint: ActivityHintType = stop.activityHint ?? ACTIVITY_HINT.BOTH;
    const showPickupServices = activityHint === ACTIVITY_HINT.PICKUP || activityHint === ACTIVITY_HINT.BOTH;
    const showDeliveryServices = activityHint === ACTIVITY_HINT.DELIVERY || activityHint === ACTIVITY_HINT.BOTH;
    const serviceIds = stop.serviceIds || [];

    const pickupServices = (serviceTypes?.PICKUP || []).filter((val: any) => !NON_STOP_SUPPORTED_SLUGS.includes(val.slug));
    const deliveryServices = (serviceTypes?.DELIVERY || []).filter((val: any) => !NON_STOP_SUPPORTED_SLUGS.includes(val.slug));

    const mapAddress = stop.fullAddress
        ? {
              fullAddress: stop.fullAddress,
              city: stop.city,
              state: stop.state,
              stateCode: stop.stateCode,
              country: stop.country,
              countryCode: stop.countryCode,
              latitude: String(stop.latitude ?? ''),
              longitude: String(stop.longitude ?? ''),
          }
        : null;

    const renderServiceCheckboxes = (list: any[]) => (
        <div className="rounded-lg border border-utilityGray200 bg-white flex flex-wrap p-3 gap-3">
            {list.map((val: any) =>
                val?.type !== 'OTHER' || val?.slug === 'load_insurance' || val.loadLinkTerm !== null ? (
                    <div className="flex w-[calc(33.33%_-_8px)]" key={`booking_stop_service_${val.id}`}>
                        <CheckBox
                            id={`booking_stop_service_${stop.position}_${val.id}`}
                            onChangeFunc={() => {}}
                            name={val.slug}
                            value={val.id}
                            checked={serviceIds.includes(val.id)}
                            disabled
                            parentClassName="mt-px"
                        />
                        <div className="pl-2 leading-4 text-textSecondary text-xs font-medium truncate flex-1 opacity-70">
                            <ServiceLabel htmlFor={`booking_stop_service_${stop.position}_${val.id}`} name={val.name} />
                        </div>
                    </div>
                ) : null
            )}
        </div>
    );

    return (
        <>
            {/* Activity hint — read-only, mirrors StopDetails.tsx's "At this stop" pills exactly */}
            <div className="stepper-content-container">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common">{t('MyQuote.What_Happens_Here')}</label>
                </div>
                <div className="flex-1">
                    <div className="flex flex-wrap gap-2">
                        {ACTIVITY_HINT_OPTIONS.map((hintOption) => {
                            const isSelected = activityHint === hintOption;

                            return (
                                <ButtonCmp key={hintOption} type="button" className={isSelected ? 'btn-outline-primary' : 'btn_secondary_black'} disabled>
                                    {getActivityHintLabel(t, hintOption)}
                                </ButtonCmp>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Address — client decision: same CardWithOptions look Shipper/Consignee use for their
                own selected-address card (showOptions=false gets the same blue info icon, no
                switch/edit/delete menu since this is read-only), map fused right below it via the
                same two-row "field-without-top-border field-within-same-field" pattern
                ShipperConsigneeDetails.tsx uses — not a plain unbordered text block. */}
            <div className="border-t border-utilityGray200 stepper-content-container">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common">{t('MyQuote.Stop_Address')}</label>
                </div>
                <div className="flex-1">
                    <CardWithOptions
                        handleActionType={() => () => {}}
                        options={[]}
                        showOptions={false}
                        headerContent={
                            <div className="flex-1">
                                <h6 className="text-grayLight900 text-sm font-medium">{stop.companyName || '-'}</h6>
                                <p className="text-grayLight600 text-xs leading-[1.5] font-normal">{stop.fullAddress || '-'}</p>
                            </div>
                        }
                    />
                </div>
            </div>

            {mapAddress && (
                <div className="field-without-top-border field-within-same-field !pt-0">
                    <div className="stepper-content-container">
                        <div className="stepper-lable-wrap flex">
                            <></>
                        </div>
                        <div className="rounded-lg shadow-[0px_4px_8px_-2px_rgba(16,_24,_40,_0.10),_0px_2px_4px_-2px_rgba(16,_24,_40,_0.06)] overflow-hidden w-full">
                            <MapBox
                                draggable={false}
                                address={mapAddress}
                                id={`booking-stop-${stop.position}-map`}
                                className="rounded-lg bg-white h-full border border-utilityGray200"
                                isDefaultSatelliteView
                            />
                        </div>
                    </div>
                </div>
            )}

            {showPickupServices && (
                <div className={`border-t border-utilityGray200 stepper-content-container ${!showDeliveryServices ? 'border-b pb-5' : ''}`}>
                    <div className="stepper-lable-wrap flex">
                        <label className="stepper-label-common flex items-center gap-x-0.5">
                            {t('QuotingHub.Common.Pickup_Additional_Services')}
                            <div className="cursor-pointer" onClick={() => setServiceInfoModal('pickup')}>
                                <InfoCircle className="h-3.5 w-3.5 cursor-pointer text-primary" />
                            </div>
                        </label>
                    </div>
                    <div className="flex-1">{renderServiceCheckboxes(pickupServices)}</div>
                </div>
            )}

            {showDeliveryServices && (
                <div className="border-t border-utilityGray200 stepper-content-container border-b pb-5">
                    <div className="stepper-lable-wrap flex">
                        <label className="stepper-label-common flex items-center gap-x-0.5">
                            {t('QuotingHub.Common.Delivery_Additional_Services')}
                            <div className="cursor-pointer" onClick={() => setServiceInfoModal('delivery')}>
                                <InfoCircle className="h-3.5 w-3.5 cursor-pointer text-primary" />
                            </div>
                        </label>
                    </div>
                    <div className="flex-1">{renderServiceCheckboxes(deliveryServices)}</div>
                </div>
            )}

            {serviceInfoModal === 'pickup' && <AdditionalServiceModal handleClose={() => setServiceInfoModal(null)} services={pickupServices} title={`Pickup ${t('Common.Additional_Services')}`} />}
            {serviceInfoModal === 'delivery' && (
                <AdditionalServiceModal handleClose={() => setServiceInfoModal(null)} services={deliveryServices} title={`Delivery ${t('Common.Additional_Services')}`} />
            )}
        </>
    );
};

export default BookingStopDetails;
